// frontend/src/components/Individual/VoiceEnrollment.jsx
import React, { useState, useRef, useEffect } from 'react';
import { buildFingerprint, saveFingerprint, clearFingerprint, hasFingerprint } from '../../utils/speakerDetector';
import { useAudioDevices } from '../../hooks/useAudioDevices';
import AudioDevicePicker from '../Shared/AudioDevicePicker';

const DURATION_MS = 8000;

const VoiceEnrollment = ({ onClose, onEnrolled }) => {
  const [phase, setPhase]         = useState('intro');
  const [progress, setProgress]   = useState(0);
  const [countdown, setCountdown] = useState(Math.ceil(DURATION_MS / 1000));
  const [enrolled, setEnrolled]   = useState(hasFingerprint());
  const [errMsg, setErrMsg]       = useState('');

  const streamRef   = useRef(null);
  const audioCtxRef = useRef(null);
  const tickRef     = useRef(null);

  const { devices, deviceId, selectDevice, getAudioConstraints, refreshAfterPermission } = useAudioDevices();

  useEffect(() => () => cleanup(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanup = () => {
    clearInterval(tickRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioCtxRef.current?.state !== 'closed' && audioCtxRef.current?.close().catch(() => {});
  };

  const startRecording = async () => {
    try {
      setErrMsg('');
      const constraints = getAudioConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Refresh device list now that permission is confirmed
      refreshAfterPermission();

      const nativeSampleRate = stream.getAudioTracks()[0]?.getSettings()?.sampleRate || 16000;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: nativeSampleRate });
      audioCtxRef.current = audioCtx;

      setPhase('recording');
      setProgress(0);
      setCountdown(Math.ceil(DURATION_MS / 1000));

      let elapsed = 0;
      tickRef.current = setInterval(() => {
        elapsed += 1000;
        setCountdown(Math.max(0, Math.ceil((DURATION_MS - elapsed) / 1000)));
      }, 1000);

      const fingerprint = await buildFingerprint(stream, audioCtx, DURATION_MS, p => setProgress(Math.round(p * 100)));

      clearInterval(tickRef.current);
      cleanup();
      saveFingerprint(fingerprint);
      setEnrolled(true);
      setPhase('done');
      onEnrolled?.();
    } catch (err) {
      cleanup();
      if (err.name === 'NotAllowedError') {
        setErrMsg('Microphone permission denied. Please allow access and try again.');
      } else if (err.name === 'OverconstrainedError') {
        setErrMsg('Selected device is not available. Choose a different device and try again.');
      } else {
        setErrMsg(`Error: ${err.message}`);
      }
      setPhase('error');
    }
  };

  const handleClear = () => {
    clearFingerprint();
    setEnrolled(false);
    setPhase('intro');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-3">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Voice Training</h2>
          <p className="text-sm text-gray-500 mt-1">Teach the AI to recognise your voice — no API key needed</p>
        </div>

        {/* ── Intro ── */}
        {phase === 'intro' && (
          <div className="space-y-5">
            <div className="bg-indigo-50 rounded-xl p-4 text-sm text-indigo-800 space-y-2">
              <p className="font-semibold">How it works:</p>
              <ol className="space-y-1.5 list-none">
                {[
                  'Select the microphone you will use during the session',
                  'Click "Train My Voice" and speak naturally for 8 seconds',
                  'Your voice fingerprint is computed locally — never sent anywhere',
                  'During interviews, AI only responds to the interviewer\'s voice'
                ].map((t, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <span className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-indigo-200 text-indigo-700 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Device selector */}
            <AudioDevicePicker
              devices={devices}
              deviceId={deviceId}
              onSelect={selectDevice}
              disabled={false}
            />

            {enrolled && (
              <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-700 font-medium">Voice already trained</span>
                <button onClick={handleClear} className="ml-auto text-xs text-red-500 hover:text-red-700 underline">
                  Clear
                </button>
              </div>
            )}

            <button onClick={startRecording}
              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all shadow-lg">
              {enrolled ? 'Re-train Voice (8 seconds)' : 'Train My Voice (8 seconds)'}
            </button>
            <button onClick={onClose} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600">
              Cancel
            </button>
          </div>
        )}

        {/* ── Recording ── */}
        {phase === 'recording' && (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-200 animate-ping" />
                <div className="relative w-20 h-20 rounded-full bg-red-500 flex items-center justify-center shadow-xl">
                  <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-800">{countdown}s</p>
              <p className="text-sm text-gray-500 mt-1">Speak naturally — "Tell me about yourself…"</p>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-100"
                style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-center items-end space-x-1 h-8">
              {Array.from({ length: 9 }, (_, i) => (
                <div key={i} className="w-1.5 bg-red-400 rounded-full animate-bounce"
                  style={{ height: `${Math.random() * 24 + 8}px`, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Done ── */}
        {phase === 'done' && (
          <div className="text-center space-y-5">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">Voice Trained!</p>
              <p className="text-sm text-gray-500 mt-1">
                The AI will now ignore your voice and only respond to the interviewer.
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-sm text-green-700 text-left space-y-1">
              <p className="font-semibold">What happens next:</p>
              <p>• Open Create Room → Launch Stealth Agent</p>
              <p>• Enable AI (toggle ON)</p>
              <p>• Interviewer speaks → AI provides private answers</p>
              <p>• You speak → AI stays silent</p>
            </div>
            <button onClick={onClose}
              className="w-full py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors">
              Done
            </button>
          </div>
        )}

        {/* ── Error ── */}
        {phase === 'error' && (
          <div className="text-center space-y-5">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-700 font-medium">{errMsg || 'Training failed'}</p>
            <div className="flex space-x-3">
              <button onClick={() => { setPhase('intro'); setErrMsg(''); }}
                className="flex-1 py-2.5 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600">
                Try Again
              </button>
              <button onClick={onClose}
                className="flex-1 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VoiceEnrollment;
